// Keplr (and Ledger via Keplr) approval signing - ADR-36 `signArbitrary` (TECH-WEB-02).
// The ADR-36 sign-doc's chain_id is "" so the CHAIN_ID here only selects the Keplr
// account/prefix (any cosmos-prefix chain works; cosmoshub-4 ships enabled in Keplr).
const CHAIN_ID = "cosmoshub-4";

interface KeplrKey {
  bech32Address: string;
  pubKey: Uint8Array;
}
interface KeplrSignature {
  signature: string; // base64 of the 64-byte secp256k1 r||s
  pub_key: { type: string; value: string }; // value = base64 of the 33-byte compressed pubkey
}
interface Keplr {
  enable(chainId: string): Promise<void>;
  getKey(chainId: string): Promise<KeplrKey>;
  signArbitrary(chainId: string, signer: string, data: string): Promise<KeplrSignature>;
}

function getKeplr(): Keplr {
  const k = (window as unknown as { keplr?: Keplr }).keplr;
  if (!k) throw new Error("Keplr not found - install the Keplr extension and reload.");
  return k;
}

export interface KeplrApproval {
  signatureB64: string;
  /** base64 compressed pubkey - register this as the subject's publicKey (scheme secp256k1). */
  pubkeyB64: string;
  address: string;
}

/** Prompt Keplr/Ledger to sign the approval message via ADR-36; returns the signature
 *  + the signer's compressed pubkey (to register on the subject) + bech32 address. */
export async function keplrSignApproval(message: string): Promise<KeplrApproval> {
  const keplr = getKeplr();
  await keplr.enable(CHAIN_ID);
  const key = await keplr.getKey(CHAIN_ID);
  const res = await keplr.signArbitrary(CHAIN_ID, key.bech32Address, message);
  return { signatureB64: res.signature, pubkeyB64: res.pub_key.value, address: key.bech32Address };
}

/** Connect Keplr and read the account's compressed pubkey + address (no signing) -
 *  used to register an approver's key on a policy subject. */
export async function keplrGetPubkey(): Promise<{ pubkeyB64: string; address: string }> {
  const keplr = getKeplr();
  await keplr.enable(CHAIN_ID);
  const key = await keplr.getKey(CHAIN_ID);
  return { pubkeyB64: btoa(String.fromCharCode(...key.pubKey)), address: key.bech32Address };
}
